import { isFullyIslandedIntent } from '../intent/gridScope';
/**
 * PATH TO GO — unified blocker aggregator.
 *
 * Six independent "this is why the site can't go GO" sources, fused onto ONE
 * severity enum + a single ranked list. REUSE-FIRST: each source READS an
 * existing engine output (verdict, composite, firmCapacityVerdict, qaFindings,
 * coverage missing-criticals, missing pillars); nothing re-derives a blocker
 * the platform doesn't already know about.
 *
 * The six sources:
 *   1. RED PILLAR        — a pillar whose decision score > 3.2 (decision scale).
 *   2. WITHHELD / NO-GO  — the derived band is no-go OR the verdict is withheld
 *                          for inadequate data.
 *   3. ELCC CAP          — `report.firmCapacityVerdict.worstVerdict` is
 *                          marginal/infeasible (firm-capacity caps the verdict).
 *   4. QA BLOCK FINDING  — a block-severity qaFinding on any source report.
 *   5. MISSING CRIT DOC  — a critical checklist doc missing (coverage).
 *   6. MISSING PILLAR    — a scoring pillar with no real data (imputed worst).
 *
 * Pure + deterministic. Each `Blocker` carries `remediations: NextStep[]` so
 * the wizard can route the user straight to the fix.
 */
import { deriveIntentForSelectedAssessment, GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE } from '../generationImpact/selectedAssessmentIntent';
import type { GenerationAssessmentSelection } from '../../types/gridLedStandbyPlanning';
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteComposite } from '../../types/portfolioComposite';
import type { ProjectIntent, RiskItem, SiteRagReport } from '../../types/siteRag';
import {
  COARSE_CONFIDENCE_WITHHELD_REASON,
  type DerivedSiteVerdict,
} from '../portfolio/deriveSiteVerdict';
import type { ReportVerdict } from '../reportVerdict';
import { summarizeIntentFeasibility } from '../phaseFeasibility';
import { intentHasExplicitGasFreePowerMix } from '../projectIntent';
import { formatDroughtExposedSources, formatWaterSources, hasDroughtExposedSource, waterSourceKinds } from '../intent/waterSource';
import {
  toDecisionScore,
  type DecisionBandConfig,
  type DecisionBandId,
  DECISION_BAND_LABEL,
  DECISION_BAND_SHORT_LABEL,
} from '../portfolio/decisionBands';
import {
  PILLARS,
  PILLAR_IDS,
  isAdvisoryPillar,
  isDecisionApplicablePillar,
} from '../portfolio/pillarRegistry';
import { RED_PILLAR_DECISION_THRESHOLD } from '../portfolio/portfolioConfig';
import type { NextStep, NextStepSeverity } from './nextStep';
import {
  explicitUnknownIntentRecommendation,
  isIntentFieldExplicitlyUnknown,
} from '../intent/explicitUnknown';

/** Normalized blocker severity (shared with NextStep semantics). */
export type BlockerSeverity = 'critical' | 'significant' | 'noted';

/**
 * #26 — Specialist-sourcing recommendation. When a binding constraint or a
 * low-confidence gate cannot be cleared with data the platform already holds,
 * the user must SOURCE it from a specialist. This names the specific
 * deliverable that would clear THIS constraint — mirroring the SED field
 * editor's "upload supporting document / obtain verified value" affordance.
 */
export interface SpecialistSourcing {
  /** The named deliverable to obtain, e.g. "Phase I Environmental Site Assessment (ESA)". */
  deliverable: string;
  /** Who produces it, e.g. "an environmental consultant". */
  specialist: string;
  /** One-line action: "Obtain <deliverable> from <specialist> to clear this constraint." */
  recommendation: string;
}

export interface Blocker {
  /** Stable, source-namespaced id. */
  id: string;
  severity: BlockerSeverity;
  title: string;
  /** Why this blocks GO (actionable). */
  why: string;
  /** The band resolving this blocker could lift the site to, when known. */
  liftsBandTo?: DecisionBandId;
  /** Concrete next steps that clear (or progress) the blocker. */
  remediations: NextStep[];
  /**
   * RP3 — the matching Data-Trust ledger / source-report id this blocker was
   * derived from, so a resolved blocker can clear the corresponding finding in
   * lock-step (shared-id clearance) rather than waiting for a full audit re-run.
   * Populated for `qa-block:*` (the source report id) and `bindingConstraint:*`
   * (the constraint slug) where a stable id exists; absent for aggregate /
   * derived blockers that have no single ledger row. Net-new + optional —
   * byte-stable for callers that ignore it.
   */
  findingId?: string;
  /**
   * #26 — the specialist deliverable that clears this binding constraint, when
   * the gap is a data/diligence sourcing gap (NOT, e.g., an internal QA
   * conflict the user adjudicates in-app, or a commercial milestone secured by
   * the developer). Absent when the blocker is cleared by an in-platform action
   * rather than by sourcing external diligence data.
   */
  specialistSourcing?: SpecialistSourcing;
}

/**
 * RED-PILLAR threshold on the DECISION scale. A pillar whose decision score
 * exceeds this is a "red pillar" — it is materially dragging the verdict toward
 * NO-GO. 3.2 sits inside the CONDITIONAL-HIGH band (3.01–4.0): the pillar is
 * worse than merely "conditional".
 */
export { RED_PILLAR_DECISION_THRESHOLD };

const SEVERITY_RANK: Record<BlockerSeverity, number> = {
  critical: 2,
  significant: 1,
  noted: 0,
};

/* ── #26 Specialist-sourcing deliverables ──────────────────────────────── */

/**
 * Per-pillar specialist deliverable that clears a binding/low-confidence
 * constraint on that pillar. Each entry names the SPECIFIC report or study a
 * specialist would produce, plus the discipline that produces it — so a
 * "missing pillar" / "red pillar" blocker can recommend exactly what to source
 * rather than a vague "get more data". Investment-grade diligence vocabulary
 * (Phase I ESA, geotechnical report, ELCC study, etc.).
 */
const PILLAR_SPECIALIST_DELIVERABLE: Record<
  Exclude<keyof typeof PILLARS, 'evidenceQuality'>,
  { deliverable: string; specialist: string }
> = {
  power: {
    deliverable: 'a utility System Impact Study (SIS) / firm-capacity (ELCC) study',
    specialist: 'the interconnecting utility or an independent power-systems engineer',
  },
  interconnection: {
    deliverable: 'a utility interconnection feasibility / System Impact Study with a queue position',
    specialist: 'the interconnecting utility / ISO',
  },
  water: {
    deliverable: 'a water-availability / consumptive-use right confirmation and a hydrogeological study',
    specialist: 'a water-rights attorney and a hydrogeologist',
  },
  land: {
    deliverable: 'a geotechnical investigation report and an ALTA/boundary survey',
    specialist: 'a geotechnical engineer and a licensed land surveyor',
  },
  environmental: {
    deliverable: 'a Phase I Environmental Site Assessment (ESA) and a NEPA/CEQA screening memo',
    specialist: 'an environmental consultant',
  },
  regulatory: {
    deliverable: 'a permitting-pathway / zoning entitlement opinion',
    specialist: 'a land-use / regulatory attorney',
  },
  supplyChain: {
    deliverable: 'firm equipment quotes with lead-time commitments for the long-lead generation assets',
    specialist: 'OEM / EPC vendors',
  },
  financial: {
    deliverable: 'a third-party financial model / independent engineer (IE) review',
    specialist: 'an independent engineer or financial advisor',
  },
  social: {
    deliverable: 'a stakeholder-sentiment / community-engagement assessment',
    specialist: 'a public-affairs / community-relations specialist',
  },
};

/** Compose the one-line recommendation from a deliverable + specialist. */
function sourcingRec(deliverable: string, specialist: string): SpecialistSourcing {
  return {
    deliverable,
    specialist,
    recommendation: `Obtain ${deliverable} from ${specialist} to clear this constraint.`,
  };
}

/**
 * Resolve the specialist-sourcing recommendation for a blocker, when the
 * blocker is a data/diligence SOURCING gap. Returns `undefined` for blockers
 * cleared by an in-platform action (QA-conflict adjudication, pillar-score
 * improvement) or by a developer-side commercial/legal milestone (securing
 * site control, signing a PPA / EPC, declaring intent fields) — those are not
 * "go source it from a specialist" gaps.
 *
 * Covers the intent-side firm-capacity (`elcc-intent:`) and the BTM
 * water-rights / interconnection gates (`btm-water:` / `btm-interconnection:`)
 * in addition to the report-derived gates, so EVERY binding diligence gate the
 * Path-to-GO / Decision-Guide rollup surfaces — whether it fires from a report
 * or from declared intent — names the specific specialist deliverable that
 * clears it (#26).
 */
export function specialistSourcingForBlocker(
  blocker: Pick<Blocker, 'id'>,
  missingCriticalDocs: readonly MissingCriticalDoc[] = [],
): SpecialistSourcing | undefined {
  const { id } = blocker;

  // Missing-pillar / red-pillar → the pillar's specialist deliverable.
  const pillarMatch = id.match(/^(?:missing-pillar|red-pillar):(.+)$/);
  if (pillarMatch) {
    const pillarId = pillarMatch[1] as keyof typeof PILLAR_SPECIALIST_DELIVERABLE;
    const d = PILLAR_SPECIALIST_DELIVERABLE[pillarId];
    if (d) return sourcingRec(d.deliverable, d.specialist);
    return undefined;
  }

  // Missing critical document → name the exact document to obtain.
  if (id.startsWith('missing-doc:')) {
    const docId = id.slice('missing-doc:'.length);
    const doc = missingCriticalDocs.find((m) => m.id === docId);
    const label = doc?.label ?? 'the missing critical diligence document';
    return sourcingRec(label, 'the relevant specialist (engineer / consultant / counsel)');
  }

  // Firm-capacity (ELCC) cap — REPORT-derived only. A computed firm-capacity
  // shortfall on a generated report is a diligence gap: independent
  // verification is the artifact that clears it.
  //
  // The INTENT-derived cap (`elcc-intent:`, "…(declared mix)") is deliberately
  // NOT here. It fires off the mix the user DECLARED, and its own `why` says to
  // "add firm capacity or storage to lift the cap" — a scenario improvement the
  // user makes in Generation & Impact and re-solves in-platform. Recommending
  // they commission an ELCC study to clear it contradicted the rule this very
  // function documents above ("Absent when the blocker is cleared by an
  // in-platform action"), and sent the user out of the product for something the
  // product solves. Its remediations already route to Generation & Impact.
  if (id.startsWith('elcc:')) {
    return sourcingRec(
      'an independent firm-capacity (ELCC) study and N-1 contingency analysis',
      'a power-systems engineer',
    );
  }

  // BTM water-rights gate → the water pillar's water-rights / hydrogeology
  // deliverable (an unpermitted source is cleared by SOURCING a confirmed
  // right + study, not an in-app action).
  if (id.startsWith('btm-water:')) {
    const d = PILLAR_SPECIALIST_DELIVERABLE.water;
    return sourcingRec(d.deliverable, d.specialist);
  }

  // BTM interconnection-queue gate → the interconnection pillar's System
  // Impact Study deliverable.
  if (id.startsWith('btm-interconnection:')) {
    const d = PILLAR_SPECIALIST_DELIVERABLE.interconnection;
    return sourcingRec(d.deliverable, d.specialist);
  }

  // Withheld verdict (inadequate data) → the domain analyses that lift
  // adequacy. Generic because adequacy is multi-pillar; the ranked steps +
  // recommend-next set name the specific report types to run.
  if (id === 'adequacy:withheld') {
    return sourcingRec(
      'the missing domain analyses / specialist studies named in the next steps',
      'the relevant discipline specialists',
    );
  }

  // NOT sourcing gaps (cleared in-platform or by a developer commercial/legal
  // milestone, NOT by sourcing a specialist deliverable):
  //   band:no-go              → lifted by in-platform pillar improvements
  //   qa-block:*              → QA-conflict adjudication in-app
  //   btm-land:*              → secure site control (option / LOI / purchase)
  //   btm-fuel:*              → secure firm gas (operator/commercial)
  //   btm-offtake:*           → sign a contracted PPA (commercial milestone)
  //   btm-carbon:*            → re-shape the declared mix (intent edit)
  //   attestation:*           → confirm a development milestone (declared)
  //   intent-missing:*        → declare the field in intent (in-platform)
  return undefined;
}

/* ── qaFindings block counter (mirrors pillarScorer.countQaBlocks) ────── */

/**
 * Count block-severity qaFindings on a report. Mirrors the (unexported)
 * `countQaBlocks` in `pillarScorer.ts` so the blocker layer counts the SAME
 * findings the composite scorer's confidence term already penalizes. Kept in
 * sync deliberately (RULE 2: no silent divergence from the engine's view).
 */
function countQaBlocks(report: SiteRagReport): number {
  const qa = report.qaFindings;
  if (!qa) return 0;
  let blocks = 0;
  for (const v of qa.providerPrecedenceViolations ?? []) if (v.severity === 'block') blocks += 1;
  for (const v of qa.internalNumberFindings ?? []) if (v.severity === 'block') blocks += 1;
  for (const v of qa.citationIntegrityViolations ?? []) if (v.severity === 'block') blocks += 1;
  for (const v of qa.distribution ?? []) if (v.severity === 'block') blocks += 1;
  for (const v of qa.docProvenanceFindings ?? []) if (v.severity === 'block') blocks += 1;
  for (const v of qa.sectionDriftFindings ?? []) if (v.severity === 'block') blocks += 1;
  return blocks;
}

/* ── Per-source detectors ───────────────────────────────────────────── */

function redPillarBlockers(composite: SiteComposite | null): Blocker[] {
  if (!composite) return [];
  const out: Blocker[] = [];
  for (const pillarId of PILLAR_IDS) {
    // `evidenceQuality` is NOT a red-pillar candidate. It is the only pillar
    // whose score is derived from the artifact it gates: `compositeScorer`
    // overwrites it from `computeDataConfidencePenalty`, which itself starts
    // with `aggregateQaCounts(reports)` — the QA findings of the generated
    // reports. Surfacing it here restated the SAME root cause a third time,
    // alongside `adequacy:withheld` (the coarse-confidence gate) and
    // `qa-block:*` (the actionable per-report finding).
    //
    // It is also structurally unable to help: the penalty saturates at
    // MAX_DATA_COMPLETENESS_PENALTY, which pegs the decision score at the
    // DECISION_SCORE_MAX ceiling (a flat 5.00 with no gradient, so clearing
    // some-but-not-all findings never moves it), it carries no remediations,
    // and it is already excluded from PILLAR_SPECIALIST_DELIVERABLE so it can
    // never name a deliverable. It is likewise already skipped by
    // `missingPillarBlockers`. Excluding it here makes that treatment
    // consistent across all pillar-derived sources.
    if (pillarId === 'evidenceQuality') continue;
    const p = composite.pillars[pillarId];
    if (!p || p.missing) continue; // missing pillars are their own blocker source.
    // Static advisory pillars cannot move the decision. Keep them visible on
    // informational surfaces, but never turn them into Path-to-GO blockers.
    //
    // Deliberately `isAdvisoryPillar` and NOT `isDecisionApplicablePillar`: past
    // this point the pillar is MEASURED, and a measured pillar is decision
    // evidence by definition, so the site-specific applicability stamp must not
    // be consulted here at all. Consulting it is how a measured RED Financial
    // pillar lost its `red-pillar:financial` blocker when the saved model
    // stopped validating — and `financial` is not in MITIGABLE_FLOOR_SOURCES,
    // so a hard NO-GO-capable floor vanished with it.
    //
    // The real protection lives in compositeScorer, which now stamps
    // `not-applicable` only when the pillar is also `missing`. This guard is
    // the local backstop for a composite persisted under the older rule, and it
    // is order-independent on purpose — reordering it must not change behaviour.
    if (isAdvisoryPillar(pillarId)) continue;
    const decision = toDecisionScore(p.score);
    if (decision > RED_PILLAR_DECISION_THRESHOLD) {
      out.push({
        id: `red-pillar:${pillarId}`,
        severity: 'significant',
        title: `${PILLARS[pillarId].label} pillar is red`,
        why: `${PILLARS[pillarId].label} scores ${decision.toFixed(2)} on the decision scale (> ${RED_PILLAR_DECISION_THRESHOLD}). ${p.rationale}`,
        remediations: [],
      });
    }
  }
  return out;
}

function bandBlockers(verdict: DerivedSiteVerdict): Blocker[] {
  // Withheld verdict (inadequate data) — the band is null + gray. This is NOT a
  // red NO-GO; it is "close the adequacy gap first".
  if (!verdict.dataAdequate) {
    return [
      {
        id: 'adequacy:withheld',
        severity: 'critical',
        title: 'Verdict withheld — data not yet adequate',
        why:
          verdict.adequacyReason ??
          'Underlying data is too sparse / low-confidence to stand behind a GO or NO-GO call.',
        remediations: [],
      },
    ];
  }
  if (verdict.band === 'no-go') {
    return [
      {
        id: 'band:no-go',
        severity: 'critical',
        title: `Current band is ${DECISION_BAND_SHORT_LABEL.noGo}`,
        why:
          `The composite places this site in the "${DECISION_BAND_LABEL.noGo}" band. Lift the worst pillars (see ranked steps) to move it toward "${DECISION_BAND_SHORT_LABEL.go}".`,
        liftsBandTo: 'conditionalHigh',
        remediations: [],
      },
    ];
  }
  return [];
}

function elccBlockers(reports: readonly SiteRagReport[]): Blocker[] {
  // Worst firm-capacity verdict across all source reports.
  let worst: 'marginal' | 'infeasible' | null = null;
  for (const r of reports) {
    const v = r.firmCapacityVerdict?.worstVerdict;
    if (v === 'infeasible') {
      worst = 'infeasible';
      break;
    }
    if (v === 'marginal' && worst == null) worst = 'marginal';
  }
  if (!worst) return [];
  return [
    {
      id: `elcc:${worst}`,
      severity: worst === 'infeasible' ? 'critical' : 'significant',
      title:
        worst === 'infeasible'
          ? 'Firm-capacity (ELCC) cannot serve the 24/7 load'
          : 'Firm-capacity (ELCC) is short of planning reserve',
      why:
        worst === 'infeasible'
          ? 'The declared power mix cannot firm the 24/7 load — add firm capacity or storage to lift the cap.'
          : 'The declared power mix covers the load but is short of planning reserve — add firm headroom to lift the cap.',
      remediations: [],
    },
  ];
}

/**
 * BTM procurement posture — mirrors `powerSetup()` / `feasibilitySetup()`'s
 * `btmStrategy` derive(): nuclear primary/considering → 'hybrid', else
 * 'grid-only'. Scopes which BTM-field gates fire (gap
 * schema-power-setup-btm-strategy): a grid-only project has no on-site
 * fuel-supply gate; a btm-only project's interconnection gate is advisory.
 * Kept as the SAME derivation so the path and the Coverage Matrix agree.
 */
type BtmStrategy = 'grid-only' | 'btm-only' | 'hybrid' | 'islanded';
function resolveBtmStrategy(intent: ProjectIntent | undefined): BtmStrategy {
  if (isFullyIslandedIntent(intent)) {
    return 'islanded';
  }
  if (
    intent?.nuclearInterest === 'primary' ||
    intent?.nuclearInterest === 'considering'
  ) {
    return 'hybrid';
  }
  return 'grid-only';
}

/* ── Intent-driven firm-capacity + BTM-field detectors ─────────────────── */

/**
 * INTENT-side firm-capacity blocker (gaps intent-firm-capacity-basis-mw /
 * verdict-cap-firm-capacity-elcc). The verdict cap (`capTierByFirmCapacity`)
 * reads `summarizeIntentFeasibility(intent).worstVerdict` directly from intent;
 * Path-to-GO must read the SAME oracle so the cap and the blocker can never
 * diverge BEFORE a power-availability report exists. When the verdict carried a
 * `firmCapacityCap`, we surface its EXACT `note` + `preCapTier` (the
 * why-capped / what-to-lift-it content) so the displayed cap and the path's
 * headline next-step are byte-identical.
 *
 * Also emits a 'missing firm-capacity basis' blocker when the project declares
 * NO firm load (powerRequirementMW ≤ 0 AND no capacityCreditIntent target) —
 * the firm-capacity gate is THE spine of every BTM decision, so its absence is
 * itself a distance-to-GO gap, visible before any report is run.
 */
function intentFirmCapacityBlockers(
  intent: ProjectIntent | undefined,
  verdict: ReportVerdict | undefined,
): Blocker[] {
  if (!intent) return [];
  const out: Blocker[] = [];

  // 1. Intent-side ELCC shortfall — SAME oracle the verdict cap uses.
  const worst = summarizeIntentFeasibility(intent).worstVerdict;
  if (worst === 'infeasible' || worst === 'marginal') {
    // Prefer the verdict cap's recorded note/preCapTier when present (so the
    // displayed cap and the path headline are byte-identical); else synthesise.
    const cap = verdict?.firmCapacityCap;
    const note =
      cap?.note ??
      (worst === 'infeasible'
        ? 'The declared power mix cannot firm the 24/7 load — add firm capacity or storage to lift the cap.'
        : 'The declared power mix covers the load but is short of planning reserve — add firm headroom to lift the cap.');
    out.push({
      id: `elcc-intent:${worst}`,
      severity: worst === 'infeasible' ? 'critical' : 'significant',
      title:
        worst === 'infeasible'
          ? 'Firm-capacity (ELCC) cannot serve the 24/7 load (declared mix)'
          : 'Firm-capacity (ELCC) is short of planning reserve (declared mix)',
      why: note,
      ...(cap?.preCapTier && cap.preCapTier !== 'none'
        ? { liftsBandTo: capTierToBand(cap.preCapTier) }
        : {}),
      remediations: [],
    });
  }

  // 2. Missing firm-capacity basis — no declared firm load at all.
  const loadMW = intent.powerRequirementMW;
  const hasLoad = typeof loadMW === 'number' && Number.isFinite(loadMW) && loadMW > 0;
  const cci = intent.capacityCreditIntent;
  const hasFirmTarget =
    !!cci && cci.target !== 'none';
  if (!hasLoad && !hasFirmTarget) {
    // The blocker holds either way. When the user RECORDED both as unknown it
    // states the open follow-up instead of asking them to declare it again.
    const loadUnknown = isIntentFieldExplicitlyUnknown(intent, 'powerRequirement');
    const targetUnknown = isIntentFieldExplicitlyUnknown(intent, 'capacityCreditIntent');
    out.push({
      id: 'intent-missing:firm-capacity-basis',
      severity: 'significant',
      title: loadUnknown && targetUnknown
        ? 'Firm-capacity basis recorded as unknown'
        : 'Firm-capacity basis not declared',
      why: loadUnknown && targetUnknown
        ? `The firm 24/7 load and the firm-capacity (ELCC) target are recorded as unknown, so the firm-capacity GO/No-Go has no denominator to grade and stays held. Follow-up: ${explicitUnknownIntentRecommendation('powerRequirement')}`
        : loadUnknown
          // Ask only for the half that is genuinely unanswered.
          ? `The firm 24/7 load is recorded as unknown (follow-up: ${explicitUnknownIntentRecommendation('powerRequirement')}) and no firm-capacity (ELCC) target is declared — the firm-capacity GO/No-Go has no denominator to grade. Declare the accreditation target, or resolve the load follow-up.`
          : targetUnknown
            ? `The firm-capacity (ELCC) target is recorded as unknown (follow-up: ${explicitUnknownIntentRecommendation('capacityCreditIntent')}) and no firm 24/7 load is declared — the firm-capacity GO/No-Go has no denominator to grade. Declare the load, or resolve the target follow-up.`
            : 'No firm 24/7 load (power requirement MW) or firm-capacity (ELCC) target is declared — the firm-capacity GO/No-Go has no denominator to grade. Declare the load + accreditation target.',
      remediations: [],
    });
  }

  return out;
}

/** Map a (capped) verdict tier to the decision band the lift would restore. */
function capTierToBand(tier: 'green' | 'amber' | 'red'): DecisionBandId | undefined {
  if (tier === 'green') return 'go';
  if (tier === 'amber') return 'conditional';
  return undefined;
}

/**
 * Direct BTM intent-field blockers (gaps intent-interconnection-status,
 * risk-seed-water-source-curtailment, risk-seed-fuel-single-supply,
 * intent-land-control, intent-offtake-…, intent-carbon-commitment). These
 * mirror the deterministic risk seeds 1:1 (typeSpecificRiskSeeds.ts) so the
 * verdict-side risks and the path's readiness gates cannot diverge — a declared
 * BTM exposure surfaces as a Path-to-GO blocker WITHOUT waiting for a report.
 *
 * Scoped by `btmStrategy`: a grid-only project's fuel-supply gate is suppressed
 * (no on-site gas firm gen); a btm-only project's interconnection gate is
 * advisory rather than blocking.
 */
function btmFieldBlockers(intent: ProjectIntent | undefined): Blocker[] {
  if (!intent) return [];
  const out: Blocker[] = [];
  const strategy = resolveBtmStrategy(intent);

  // Interconnection queue — early-queue energization risk. Suppressed for an
  // islanded (off-grid) build (zero grid interconnection), advisory for a
  // btm-only project (it does not depend on grid import), blocking otherwise.
  const q = intent.interconnectionStatus?.queueStatus;
  if (strategy !== 'islanded' && (q === 'pre-application' || q === 'queued' || q === 'study')) {
    const advisory = strategy === 'btm-only';
    out.push({
      id: `btm-interconnection:${q}`,
      severity: advisory ? 'noted' : q === 'pre-application' ? 'significant' : 'noted',
      title: advisory
        ? 'Interconnection queue early (advisory — BTM-only)'
        : 'Interconnection queue energization risk',
      why: advisory
        ? `Interconnection is ${q}; for a behind-the-meter-only build this is advisory, but a future grid tie would inherit the queue timeline.`
        : `The grid interconnection request is ${q} — multi-year study / network-upgrade timelines can slip the energization date. Advance the study or size bridging on-site generation.`,
      remediations: [],
    });
  }

  // Water source rights — unpermitted surface / groundwater is curtailment
  // exposure. Mirrors the water-curtailment risk seed.
  const ws = intent.waterSource;
  if (hasDroughtExposedSource(ws) && ws?.rightsStatus !== 'permitted') {
    out.push({
      id: `btm-water:${waterSourceKinds(ws).join('+')}`,
      severity: 'significant',
      title: 'Water rights not yet permitted',
      // Name ONLY the drought-exposed sources: with municipal+surface+reuse
      // declared this used to assert municipal and reuse are curtailment-exposed.
      why: `An unpermitted ${formatDroughtExposedSources(ws)} water source is exposed to drought curtailment of cooling withdrawal — secure a permitted / senior right or a firm municipal supply.`,
      remediations: [],
    });
  }

  // Fuel supply — long-lead, single-path gas dependency. Suppressed for a
  // grid-only build (no on-site gas firm generation to fuel).
  const fa = intent.fuelAccess;
  if (strategy !== 'grid-only' && !intentHasExplicitGasFreePowerMix(intent)
    && fa && (fa.supply === 'none' || fa.supply === 'lateral-required')) {
    out.push({
      id: `btm-fuel:${fa.supply}`,
      severity: fa.supply === 'none' ? 'significant' : 'noted',
      title: 'Fuel-supply long-lead dependency',
      why:
        fa.supply === 'none'
          ? 'No declared gas supply for on-site firm generation — a new lateral or alternative fuel is a long-lead, single-path COD dependency.'
          : 'A pipeline lateral is required — a long-lead, single-path fuel dependency that concentrates COD-schedule risk. Secure firm gas early / qualify dual-fuel.',
      remediations: [],
    });
  }

  // Offtake — merchant / unstructured revenue is a development-readiness gap
  // (a signed PPA is a milestone). Signal-gated on an explicit merchant
  // structure (contracted / hybrid are progressing; absent → undecided, no gate).
  if (intent.offtake?.structure === 'merchant') {
    out.push({
      id: 'btm-offtake:merchant',
      severity: 'noted',
      title: 'Revenue is merchant (no contracted offtake)',
      why:
        'The declared offtake is merchant — a contracted PPA is a development-readiness milestone that de-risks the revenue side of the GO decision.',
      remediations: [],
    });
  }

  // Carbon commitment vs gas firming — a clean-energy pledge can FORBID gas
  // firming, which would otherwise be the firm-capacity remediation. Surface
  // the constraint so the path's firm-capacity next-step recommends clean-firm
  // / storage instead of gas (gap intent-carbon-commitment). Signal-gated on a
  // pledge that forbids gas AND a declared gas dependency in the mix.
  if (carbonPledgeForbidsGas(intent) && declaredMixUsesGas(intent)) {
    out.push({
      id: 'btm-carbon:gas-conflict',
      severity: 'noted',
      title: 'Carbon pledge constrains the firm-capacity path',
      why: `The declared carbon commitment (${intent.carbonCommitment}) constrains gas firming — the firm-capacity path should add clean-firm (SMR / geothermal) or long-duration storage rather than gas to stay consistent with the pledge.`,
      remediations: [],
    });
  }

  return out;
}

/** A carbon pledge that forbids (or strongly disfavours) gas firming. */
function carbonPledgeForbidsGas(intent: ProjectIntent | undefined): boolean {
  const c = intent?.carbonCommitment;
  return c === 're100' || c === '24-7-cfe' || c === 'net-zero-2030';
}

/** True when the declared per-phase power mix includes a gas component. */
function declaredMixUsesGas(intent: ProjectIntent | undefined): boolean {
  for (const phase of intent?.phases ?? []) {
    for (const c of phase.powerSourceConfig?.components ?? []) {
      if (c === 'recip' || c === 'gas-turbine') return true;
    }
  }
  return false;
}

/**
 * Operator-attestation readiness blockers (gap intent-attestations). The
 * foundational legal / commercial milestones (PPA signed, SPE formed, EPC
 * signed, firm gas) are quintessential readiness gates. A milestone left
 * 'unknown' (open) or 'no' (confirmed-absent) is a distance-to-GO gap. Captured
 * in the embedded ProjectIntentForm but previously read by no Path-to-GO module.
 *
 * Signal-gated: ONLY attestations the operator has actually set to 'no' /
 * 'unknown' fire; an absent attestations map / a 'yes' / 'in-progress' /
 * 'in-discussion' state emits nothing (byte-stable for legacy intents).
 */
const ATTESTATION_LABELS: Record<string, string> = {
  ppaSigned: 'PPA signed',
  speFormed: 'SPE / project company formed',
  epcSigned: 'EPC contract signed',
  gasSupplyFirm: 'Firm gas supply secured',
  slotReservationBinding: 'Equipment slot reservation binding',
};

function attestationBlockers(intent: ProjectIntent | undefined): Blocker[] {
  const att = intent?.attestations;
  if (!att) return [];
  const out: Blocker[] = [];
  for (const [key, state] of Object.entries(att)) {
    if (state !== 'no' && state !== 'unknown') continue;
    const label = ATTESTATION_LABELS[key] ?? key;
    out.push({
      id: `attestation:${key}:${state}`,
      severity: state === 'no' ? 'significant' : 'noted',
      title:
        state === 'no'
          ? `${label} — confirmed not in place`
          : `${label} — status unknown`,
      why:
        state === 'no'
          ? `${label} is confirmed absent — a foundational development milestone the project needs before GO.`
          : `${label} status is unknown — confirm this development milestone to close the readiness gap.`,
      remediations: [],
    });
  }
  // Deterministic order (Object.entries order is insertion order, but pin it).
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

function qaBlockBlockers(reports: readonly SiteRagReport[]): Blocker[] {
  const out: Blocker[] = [];
  for (const r of reports) {
    const blocks = countQaBlocks(r);
    if (blocks > 0) {
      const typeId = (r.reportType ?? 'feasibility') as ReportTypeId;
      out.push({
        id: `qa-block:${r.id}`,
        severity: 'significant',
        title: `${blocks} blocking QA finding${blocks === 1 ? '' : 's'} on ${typeId}`,
        // These are defects in the GENERATED ARTIFACT, not gaps in the user's
        // evidence, and the copy must not imply otherwise. R-78 fires when the
        // report used an LLM value where a tier-1 provider fact already existed;
        // R-66 fires on dangling/orphan/broken citations the generator emitted;
        // the internal-number reconciler fires when one figure disagrees between
        // sections. None of them is closed by uploading a document — the remedy
        // is to re-run the analysis so the verifiers re-evaluate corrected
        // content.
        why:
          'The generated report contradicts itself or its sources (a tier-1 provider value was '
          + 'overridden, a citation does not resolve, or a figure disagrees between sections). '
          + 'These are defects in the generated analysis rather than gaps in your evidence — '
          + 're-run the analysis to clear them.',
        // RP3 — the source report id is the shared id the host clears in
        // lock-step once the underlying QA finding is resolved.
        findingId: r.id,
        // Re-running IS the remedy, so the action ships WITH the blocker rather
        // than depending on a matching entry existing in the ranked step list.
        // `attachRemediations` only ever decorates a blocker that already has
        // steps or matches a branch; a site whose ranked list happens to hold no
        // generate step would otherwise show this finding with no button at all.
        remediations: [
          {
            id: `qa-block:${r.id}:rerun`,
            kind: 'generate',
            label: 'Re-run the analysis',
            why:
              'Re-runs the verifiers (provider precedence, citation integrity, internal-number '
              + 'reconciliation) against regenerated content.',
            severity: 'significant',
            valueScore: 70,
            sourceModule: 'preflight',
            resolver: {
              label: 'Re-run analysis',
              action: 'open-tab',
              targetId: 'reports',
            },
          },
        ] as NextStep[],
      });
    }
  }
  return out;
}

/** A missing critical checklist doc, surfaced by coverage. */
export interface MissingCriticalDoc {
  id: string;
  label: string;
  reportType: ReportTypeId;
}

function missingDocBlockers(missingCriticalDocs: readonly MissingCriticalDoc[]): Blocker[] {
  return missingCriticalDocs.map((doc) => ({
    id: `missing-doc:${doc.id}`,
    severity: 'significant' as BlockerSeverity,
    title: `Missing critical document: ${doc.label}`,
    why: `A critical ${doc.reportType} checklist document is missing — its absence caps evidence confidence.`,
    remediations: [],
  }));
}

/**
 * Deterministic slug from a binding-constraint sentence, so the blocker id is
 * stable + DOM-safe (the id flows into a `data-testid`). Lowercased, non-alnum
 * runs collapsed to '-', trimmed, length-capped. Empty input → '' (caller skips).
 */
function slugifyConstraint(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/**
 * Binding-constraint blockers — the EXACT caps that actually fired in the
 * authoritative Go/No-Go rollup (`buildExecutiveDecisionRollup().bindingConstraints`),
 * mapped 1:1 onto Blockers so the Resolve stage can list every cap that lowered
 * the band. Each non-empty constraint string becomes one `significant` blocker
 * with a generic "review the binding constraint" remediation (the cap text is
 * already the actionable why; the deep-link to the audit panel is the route).
 *
 * Deterministic: deduped by slug (so the same cap text never double-counts),
 * insertion order preserved before the aggregator's final sort. Empty / blank
 * strings are skipped.
 */
/**
 * How a binding constraint from the Go/No-Go rollup can actually be cleared.
 *
 * ADJUDICABLE — a judgement cap a reviewer weighs and may clear or supersede in
 *   the decision audit. The generic audit wording is correct for these.
 * COMPUTED — a derived signal (a score, a confidence tag, a verifier output).
 *   There is NOTHING to adjudicate: it changes only when the input that
 *   produced it changes. Telling a user to "clear or supersede" one offers to
 *   override a real gate instead of fixing its cause, so each computed
 *   constraint must instead say what produced it and route to that input.
 *
 * The rollup emits constraints as free text, so recognition is still textual —
 * but it now happens ONCE, in a typed registry, instead of being pattern-matched
 * ad hoc at each consumer. Adding a computed constraint is a registry entry, and
 * anything unrecognised stays `adjudicable`, which is the pre-existing behaviour.
 *
 * The fully durable form is for the rollup to emit a structured
 * `{ id, kind, source }` per constraint rather than a sentence; this registry is
 * the seam that change would collapse into.
 */
export type BindingConstraintKind = 'computed' | 'adjudicable';

interface ComputedConstraintRule {
  /** Stable id for the rule (not the constraint slug). */
  id: string;
  /** Recognise this constraint in the rollup's free text. */
  match: (text: string) => boolean;
  /** What produced it and what actually clears it. */
  why: string;
  /**
   * An authoritative blocker that already states all of this. When present AND
   * that blocker is in the list, the rollup restatement is dropped rather than
   * shown twice with weaker wording.
   */
  restatedBy?: string;
  /** Where the user goes to change the input that produced the signal. */
  route?: { label: string; action: string; targetId?: string };
}

const COMPUTED_CONSTRAINT_RULES: readonly ComputedConstraintRule[] = [
  {
    // P1.4c (W6) — PATH 1b's withhold ("Power strategy undeclared — confirm
    // grid-only, hybrid, or BTM-only…"). It used to fall through to the
    // generic adjudicable copy — "clear or supersede it to lift the verdict" —
    // which invited a user to supersede a hold whose only remedy is filling in
    // an empty select. There is nothing to adjudicate.
    id: 'power-strategy-undeclared',
    match: (t) => /power strategy undeclared/i.test(t),
    why:
      'The accountable grid-versus-on-site power strategy has not been declared, so no GO or '
      + 'CONDITIONAL call is published. Declare grid-only, hybrid, or BTM-only in Project Intent: '
      + 'a grid-only declaration marks Generation & Impact not applicable; hybrid and BTM-only '
      + 'require a promoted generation scenario.',
    route: { label: 'Declare power strategy', action: 'open-intent' },
  },
  {
    id: 'coarse-data-confidence',
    match: (t) => t.includes(COARSE_CONFIDENCE_WITHHELD_REASON),
    why:
      'Data confidence is computed from the QA findings on this site\'s analyses — it is not a '
      + 'judgement call, so there is nothing to adjudicate here. It lifts when those findings are '
      + 'resolved and the analyses are re-run.',
    restatedBy: 'adequacy:withheld',
  },
  {
    // Same hazard as 'insufficient-decision-coverage' below, for the reason the
    // adequacy gate emits when NOTHING has been analysed. Before this rule it
    // fell through to the generic adjudicable copy, so a site with no analyses
    // announced a constraint to "clear or supersede in the decision audit" —
    // offering to lift the very gate that stops a verdict resting on nothing.
    // The other surfaces inherit that: Resolve counts it in "{n} to resolve",
    // the Verify rail shows "1 open", and the exported report prints it.
    id: 'no-source-reports',
    match: (t) => /^Insufficient evidence — No source reports yet$/i.test(t.trim()),
    why:
      'Nothing has been analysed on this site yet, so there is no evidence for a verdict to '
      + 'rest on. That is a count, not a judgement — there is no conflict here and nothing in '
      + 'the audit to clear or supersede. It lifts as soon as the first screening analysis has '
      + 'been generated.',
    route: { label: 'Open Analyses', action: 'open-tab', targetId: 'reports' },
    restatedBy: 'adequacy:withheld',
  },
  {
    // The rollup's own fallback when the composite has not been scored yet.
    id: 'composite-not-yet-computed',
    // Anchored to the adequacy gate's exact constraint. Unanchored, this also
    // matched LLM-authored risk labels from PATH 6 ("Unmitigated critical
    // finding — <reportType>: <label>"), which would have given a real finding
    // the wrong explanation AND stripped its remediation step, because a
    // computed rule with no route produces an empty remediations array.
    match: (t) => /^Insufficient evidence — Not yet computed$/i.test(t.trim()),
    why:
      'This site\'s decision composite has not been computed yet, so there is no verdict to '
      + 'state and nothing is being withheld on evidence grounds. There is nothing in the audit '
      + 'to clear. It resolves once the site\'s analyses have been scored.',
    restatedBy: 'adequacy:withheld',
  },
  {
    // SED D1.1's hard gate. This one DOES have something to settle — but on Site
    // Evidence, by adjudicating the conflict, not by superseding a cap in the
    // decision audit. The generic copy pointed at the wrong surface.
    id: 'unresolved-evidence-conflict',
    match: (t) => /^Insufficient evidence — Unresolved high-severity evidence conflict/i.test(t.trim()),
    why:
      'A provider and a document disagree materially about this site, and the disagreement has '
      + 'not been resolved. That IS the thing to settle, but it is settled on Site Evidence by '
      + 'adjudicating the conflict — not by superseding a cap in the decision audit, which does '
      + 'not change what the sources say. It lifts when every high-severity conflict is resolved.',
    route: { label: 'Open Site Evidence', action: 'open-evidence' },
    restatedBy: 'adequacy:withheld',
  },
  {
    // The adequacy gate emits its withheld reason into `bindingConstraints`,
    // which becomes a `bindingConstraint:` blocker 1:1. With no rule matching,
    // it fell through to the generic adjudicable copy — "review it in the
    // decision audit and clear or supersede it to lift the verdict" — which
    // invites a user to SUPERSEDE the very gate that exists to stop a verdict
    // being asserted on absent evidence. There is nothing to adjudicate: both
    // floors are arithmetic over which pillars are measured.
    //
    // `restatedBy: 'adequacy:withheld'` dedups it against the adequacy blocker
    // that already says the same thing, exactly as 'coarse-data-confidence'
    // does above. Matches BOTH coverage reasons (deriveSiteVerdict emits
    // "…% of decision pillars have data" and "…% of decision weight is
    // measured").
    id: 'insufficient-decision-coverage',
    match: (t) => /decision weight is measured|decision pillars have data/i.test(t),
    // The advice used to read "prioritise the heaviest unmeasured pillars
    // (power, financial, water, regulatory)". That is wrong for exactly the
    // site this rule was written to catch: on a feasibility-only site, power
    // and financial are ALREADY measured — feasibility feeds both — and their
    // dedicated reports therefore add +0.00 coverage. The copy named the two
    // pillars no user action could move, which is the same defect the 0.65
    // commit set out to remove and then reintroduced in its own remediation
    // text.
    why:
      'This is arithmetic, not a judgement: it counts which decision pillars carry measured '
      + 'evidence and how much of the decision WEIGHT they represent. There is nothing to '
      + 'supersede in the audit. It lifts when a report is generated for a domain nothing '
      + 'currently measures — environmental, water, supply chain or community sentiment. '
      + 'Re-running an analysis for a domain already covered adds no coverage, however heavy '
      + 'that pillar is: feasibility already measures power, land, regulatory and financial, so '
      + 'those reports move the number by nothing.',
    restatedBy: 'adequacy:withheld',
  },
  {
    id: 'firm-capacity-declared-mix',
    match: (t) => /firm[- ]capacity|ELCC/i.test(t) && /declared mix/i.test(t),
    why:
      'This is computed from the generation mix declared in Project Intent — the firm-capacity '
      + 'oracle re-evaluates it whenever that mix changes. Change the declared mix (add firm '
      + 'capacity or storage) in Project Intent, then re-solve in Generation & Impact. It cannot '
      + 'be superseded in the audit, because the audit does not change the mix.',
    route: { label: 'Open Project Intent', action: 'open-intent' },
  },
];

function classifyBindingConstraint(text: string): ComputedConstraintRule | null {
  return COMPUTED_CONSTRAINT_RULES.find((rule) => rule.match(text)) ?? null;
}

/**
 * D17 (audit 2026-08-19) — the FINANCIAL source floor.
 *
 * `buildExecutiveDecisionRollup` emits its per-source floor as free text:
 *   "Source floor — the financial report reads NO-GO; the overall call cannot
 *    exceed it."
 * That is the CAP, not the cause, so a red Financial Viability report fell
 * through to the generic "review it in the decision audit and clear or
 * supersede it" copy — advice that is wrong twice over: the financial band is a
 * deterministic engine output (there is no judgement to supersede), and the
 * thing that would actually lift it lives in the Financial Model, not the audit.
 *
 * The stamp already names WHY: `financialAnalysis.bindingConstraints` (e.g.
 * "Negative project NPV", "IRR materially below hurdle"), written by
 * `scoreSiteFinancial`. Surface those verbatim.
 */
function isFinancialSourceFloor(text: string): boolean {
  return /source floor/i.test(text) && /\bfinancial report\b/i.test(text);
}

/** The financial report's own stamp, newest-first across the site's reports. */
function financialStamp(
  reports: readonly SiteRagReport[],
): SiteRagReport['financialAnalysis'] | null {
  let best: SiteRagReport['financialAnalysis'] | null = null;
  let bestAt = '';
  for (const r of reports) {
    if ((r.reportType ?? '') !== 'financial') continue;
    const stamp = r.financialAnalysis;
    if (!stamp) continue;
    const at = stamp.computedAt ?? r.createdAt ?? '';
    if (best === null || at >= bestAt) {
      best = stamp;
      bestAt = at;
    }
  }
  return best;
}

const FINANCIAL_MODEL_ROUTE = {
  label: 'Open Financial Model',
  action: 'open-tab' as const,
  targetId: 'financial',
};

/**
 * The financial-specific `why` for a financial source-floor constraint, naming
 * the engine's OWN binding constraints. Returns null when the site has no
 * financial stamp to name (then the generic copy still applies).
 */
function financialFloorWhy(reports: readonly SiteRagReport[]): string | null {
  const stamp = financialStamp(reports);
  if (!stamp) return null;
  const named = (stamp.bindingConstraints ?? [])
    .map((c) => c.trim())
    .filter(Boolean);
  const verdict = stamp.band === 'red'
    ? 'Unfinanceable as structured'
    : stamp.band === 'amber'
      ? 'Conditional'
      : stamp.band === null
        ? 'withheld'
        : 'Viable';
  const cause = named.length > 0
    ? `The financial engine names what is binding it: ${named.join('; ')}.`
    : stamp.verdictWithheldReason === 'inputs-incoherent'
      ? 'The financial model computes, but its CAPEX basis and its revenue basis describe different plant, so the engine withheld the band rather than publish a misleading return.'
      : 'The financial model cannot yet compute a return, so the engine withheld the band.';
  return `The Financial Viability report reads ${verdict}, and that floors the overall call. `
    + `${cause} These are deterministic engine outputs, not audit judgements, so the decision `
    + 'audit cannot move them. The band lifts when the Financial Model changes (re-cost CAPEX, '
    + 're-price or re-term the PPA, restructure the debt:equity stack) and the Financial '
    + 'Viability report is regenerated.';
}

function bindingConstraintBlockers(
  constraints: readonly string[],
  reports: readonly SiteRagReport[] = [],
): Blocker[] {
  const out: Blocker[] = [];
  const seen = new Set<string>();
  // Resolved once — every financial source-floor constraint names the same stamp.
  const financialWhy = constraints.some(isFinancialSourceFloor)
    ? financialFloorWhy(reports)
    : null;
  for (const raw of constraints) {
    const text = raw.trim();
    if (!text) continue;
    const slug = slugifyConstraint(text) || `c${out.length}`;
    if (seen.has(slug)) continue;
    seen.add(slug);
    // A financial source floor is COMPUTED, with its own dynamic cause text, so
    // it takes precedence over the static rule table (which cannot name the
    // stamp's constraints).
    const financialFloor = financialWhy != null && isFinancialSourceFloor(text);
    const computed = financialFloor
      ? { id: 'financial-source-floor', why: financialWhy!, route: FINANCIAL_MODEL_ROUTE }
      : classifyBindingConstraint(text);
    out.push({
      id: `bindingConstraint:${slug}`,
      severity: 'significant',
      title: text,
      // ADJUDICABLE keeps the audit wording. COMPUTED must not: it has no
      // judgement to make, so it states what produced it and where to change it.
      why: financialFloor
        ? financialWhy!
        : computed
          ? `${computed.why} This constraint is currently binding the Go/No-Go band.`
          : 'This constraint is currently binding the Go/No-Go band — review it in the decision '
            + 'audit and clear or supersede it to lift the verdict.',
      // RP3 — the constraint slug is the shared id the host clears once the
      // cap is reviewed / superseded.
      findingId: `bindingConstraint:${slug}`,
      remediations: computed
        ? (computed.route
            ? [{
                id: `bindingConstraint:${slug}:fix`,
                kind: 'intent' as const,
                label: computed.route.label,
                why: computed.why,
                severity: 'significant' as const,
                valueScore: 60,
                sourceModule: 'preflight' as const,
                resolver: {
                  label: computed.route.label,
                  action: computed.route.action,
                  ...(computed.route.targetId ? { targetId: computed.route.targetId } : {}),
                },
              }] as NextStep[]
            : [])
        : [
            {
              id: `bindingConstraint:${slug}:review`,
              kind: 'resolve',
              label: 'Review the binding constraint',
              why: text,
              severity: 'significant',
              valueScore: 50,
              sourceModule: 'preflight',
            },
          ],
    });
  }
  return out;
}

/**
 * Deterministic risk-seed blockers (gap risk-seed-merge). The P3-7 mechanic
 * (`buildTypeSpecificRiskSeeds` → `mergeRiskSeeds`) folds the decisive,
 * provenance-stamped, intent-derived risk seeds (firm-capacity,
 * interconnection-queue, water-source-curtailment, fuel-single-supply) into
 * `report.risks`. Path-to-GO previously ignored them entirely, so four of the
 * five gated readiness gates never surfaced even AFTER a report existed. This
 * surfaces them: critical/high-severity intent-sourced risks become Blockers,
 * with the seed's mitigation carried as the remediation hint.
 *
 * Deduped by the seed's stable id (so the same seed across multiple reports is a
 * single blocker). The intent-side detectors (`btmFieldBlockers`) already cover
 * the pre-report case from intent directly; this covers the post-report case
 * where the LLM may also have authored the same risk — `dedupeBlockers` in the
 * aggregator collapses any overlap.
 */
function riskSeedBlockers(reports: readonly SiteRagReport[]): Blocker[] {
  const out: Blocker[] = [];
  const seen = new Set<string>();
  for (const r of reports) {
    for (const risk of (r.risks ?? []) as RiskItem[]) {
      if (risk.source !== 'intent') continue;
      if (risk.severity !== 'critical' && risk.severity !== 'high') continue;
      const id = `risk-seed:${risk.id}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        severity: risk.severity === 'critical' ? 'critical' : 'significant',
        title: risk.label,
        why: risk.impact || risk.label,
        remediations: [],
      });
    }
  }
  return out;
}

function missingPillarBlockers(composite: SiteComposite | null): Blocker[] {
  if (!composite) return [];
  const out: Blocker[] = [];
  for (const pillarId of PILLAR_IDS) {
    const p = composite.pillars[pillarId];
    // evidenceQuality is always overwritten + never "missing" in the output; it
    // is a derived pillar, not a data-collection gap. Skip it.
    if (pillarId === 'evidenceQuality') continue;
    // Static advisory pillars and site-specific inactive pillars (for example
    // Financial before a model exists, or Supply Chain before a BOM exists)
    // are later/optional work, not missing decision evidence.
    if (!isDecisionApplicablePillar(pillarId, p)) continue;
    if (p?.missing) {
      out.push({
        id: `missing-pillar:${pillarId}`,
        severity: 'significant',
        title: `${PILLARS[pillarId].label} has no data`,
        why: `The ${PILLARS[pillarId].label} pillar is imputed worst-case because no source analysis scores it — run its domain analysis to replace the placeholder.`,
        remediations: [],
      });
    }
  }
  return out;
}

/* ── Aggregator ─────────────────────────────────────────────────────── */

export interface BuildBlockersInput {
  generationAssessmentSelection?: GenerationAssessmentSelection;
  verdict: DerivedSiteVerdict;
  composite: SiteComposite | null;
  reports: readonly SiteRagReport[];
  missingCriticalDocs?: readonly MissingCriticalDoc[];
  /** Ranked next steps; each blocker is matched to its remediations by domain/id. */
  steps?: readonly NextStep[];
  bandConfig?: DecisionBandConfig | null;
  /**
   * Project intent — threaded so the firm-capacity + BTM-field gates are visible
   * BEFORE a report exists, reading the SAME `summarizeIntentFeasibility` oracle
   * the verdict cap uses (consistency mandate). Absent → no intent-side blockers
   * (byte-stable with the pre-existing six-source behaviour).
   */
  intent?: ProjectIntent;
  /**
   * Display verdict (`getDisplayVerdict`) — its `firmCapacityCap` note + preCapTier
   * are surfaced 1:1 in the intent-side firm-capacity blocker so the displayed
   * cap and the path headline are byte-identical. Optional.
   */
  verdictDisplay?: ReportVerdict;
  /**
   * The EXACT caps that fired in the authoritative Go/No-Go rollup
   * (`buildExecutiveDecisionRollup().bindingConstraints`). Each non-empty string
   * becomes one `bindingConstraint:*` Blocker so the Resolve stage surfaces every
   * cap that lowered the band. Absent → no binding-constraint blockers
   * (byte-stable with the pre-existing behaviour).
   */
  bindingConstraints?: readonly string[];
}

/**
 * Attach remediations to blockers by a light heuristic: a missing-pillar /
 * red-pillar blocker pulls in evidence/sed steps; a missing-doc blocker pulls
 * coverage steps for the same domain. We deliberately keep this conservative —
 * remediations are a HINT, not an exhaustive mapping.
 */
function attachRemediations(blocker: Blocker, steps: readonly NextStep[]): Blocker {
  if (steps.length === 0) return blocker;
  let matched: NextStep[] = [];
  if (blocker.id.startsWith('missing-doc:') || blocker.id.startsWith('missing-pillar:')) {
    matched = steps.filter((s) => s.kind === 'evidence' || s.kind === 'resolve');
  } else if (blocker.id.startsWith('adequacy:') || blocker.id === 'band:no-go') {
    // The whole ranked list remediates a withheld/no-go state.
    matched = [...steps];
  } else if (blocker.id.startsWith('qa-block:')) {
    // Intentionally NO match: `qaBlockBlockers` ships its own "Re-run the
    // analysis" step, which is the only thing that clears these findings.
    // Matching here would REPLACE it (`attachRemediations` overwrites when it
    // matches) with generic ranked steps, which is how "Open Site Evidence"
    // ended up blaming the user's data for a generator defect.
    matched = [];
  } else if (blocker.id.startsWith('elcc:') || blocker.id.startsWith('elcc-intent:')) {
    matched = steps.filter((s) => s.sourceModule === 'intent-recommender' || s.kind === 'generate');
  } else if (
    blocker.id.startsWith('btm-') ||
    blocker.id.startsWith('intent-missing:') ||
    blocker.id.startsWith('attestation:') ||
    blocker.id.startsWith('risk-seed:')
  ) {
    // Intent / development-readiness gates remediate via intent + evidence steps.
    matched = steps.filter((s) => s.kind === 'intent' || s.kind === 'evidence');
  }
  return matched.length > 0 ? { ...blocker, remediations: matched.slice(0, 5) } : blocker;
}

/**
 * Build the unified, ranked blocker list. Severity-first, then band-lift, then
 * a stable id tie-break. Deterministic for identical inputs.
 */
export function buildBlockers(input: BuildBlockersInput): Blocker[] {
  // `bandConfig` is accepted on the input for forward-compatibility (callers
  // thread the live decision-band config) but the current blocker detectors key
  // off the DECISION scale, which is band-config-independent.
  const {
    verdict,
    composite,
    reports,
    missingCriticalDocs = [],
    steps = [],
    intent: canonicalIntent,
    verdictDisplay,
    bindingConstraints = [],
  } = input;
  const intent = deriveIntentForSelectedAssessment(canonicalIntent, input.generationAssessmentSelection);

  const all: Blocker[] = dedupeFirmCapacity([
    ...bandBlockers(verdict),
    // Intent-side firm-capacity (SAME oracle as the verdict cap) FIRST so it
    // wins over the report-level `elccBlockers` when both fire on the same site.
    ...intentFirmCapacityBlockers(intent, verdictDisplay),
    ...(input.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? [{
      id: 'planning:firm-capacity-unqualified', severity: 'significant' as const,
      title: 'Firm-capacity qualification remains indeterminate', why: GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE, remediations: [],
    }] : []),
    ...(input.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? [] : elccBlockers(reports)),
    ...btmFieldBlockers(intent),
    ...attestationBlockers(intent),
    ...riskSeedBlockers(reports),
    ...redPillarBlockers(composite),
    ...qaBlockBlockers(reports),
    ...missingDocBlockers(missingCriticalDocs),
    ...missingPillarBlockers(composite),
    // The exact caps that fired in the authoritative Go/No-Go rollup — surfaced
    // so the Resolve stage lists every constraint binding the band. `reports` is
    // threaded so a FINANCIAL source floor can name the engine's own binding
    // constraints instead of the generic "clear or supersede" copy (D17).
    ...bindingConstraintBlockers(bindingConstraints, reports),
  ]);

  // Drop derived restatements FIRST, then let the surviving authoritative
  // blockers name their root cause.
  const deduped = dropRestatedComputedConstraints(all, steps);

  const attributed = attributeCoarseConfidenceToReportQa(deduped, reports)
    .map((b) => attachRemediations(b, steps))
    // #26 — attach the specialist-sourcing recommendation to every binding
    // constraint that is a data/diligence sourcing gap (each unmet constraint
    // → "obtain [specific specialist deliverable] to clear").
    .map((b) => {
      const sourcing = specialistSourcingForBlocker(b, missingCriticalDocs);
      return sourcing ? { ...b, specialistSourcing: sourcing } : b;
    });

  // Stable rank: severity desc, then a deterministic id tie-break. (Band-lift is
  // sparse on blockers; severity already dominates the GO-gate ordering.)
  return attributed.sort((a, b) => {
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Attribute a COARSE data-confidence gate to its actual root cause.
 *
 * The data-confidence penalty is computed by `computeDataConfidencePenalty`,
 * which begins with `aggregateQaCounts(reports)` — so block-severity QA
 * findings on the generated reports are what drive the 'C' tag that withholds
 * the verdict. Presented raw, one problem read as two unrelated constraints
 * (three, before `evidenceQuality` was excluded from `redPillarBlockers`): the
 * user saw a confidence gate, a red pillar and a QA count, and could not tell
 * that clearing the QA findings resolves all of them.
 *
 * Unlike `dedupeFirmCapacity` this does NOT drop a blocker. Both survivors are
 * load-bearing and neither can stand in for the other: `adequacy:withheld` is
 * the critical gate that actually withholds the verdict, while `qa-block:*`
 * carries the per-report `findingId` used for RP3 shared-id clearance. Deleting
 * either would lose the gate or lose lock-step clearance. Instead the gate is
 * made to NAME its cause, so the list tells one causal story with one action.
 *
 * Only fires for the coarse-confidence reason. 'No source reports yet',
 * unresolved-conflict and coverage withholds have different causes and are left
 * exactly as they are. Pure, order-preserving.
 */
/**
 * Drop the binding-constraint RESTATEMENT of the coarse-confidence gate.
 *
 * `bindingConstraintBlockers` wraps raw constraint strings from the Go/No-Go
 * rollup in a generic template: "review it in the decision audit and clear or
 * supersede it to lift the verdict". That advice is right for a JUDGEMENT cap
 * the user adjudicates, and wrong for this one — data-confidence coarseness is
 * a COMPUTED signal derived from report QA findings, so there is nothing to
 * adjudicate. Inviting the user to "supersede" it offers to override a real
 * data-quality gate instead of fixing the evidence that caused it.
 *
 * It is also a straight duplicate: `adequacy:withheld` already carries the same
 * reason at CRITICAL severity, with remediations, and (since
 * `attributeCoarseConfidenceToReportQa`) with its actual root cause named. This
 * follows `dedupeFirmCapacity`: drop the derived restatement, keep the
 * authoritative blocker.
 *
 * Only fires when that gate is actually present, and only for constraints that
 * restate its reason — every other binding constraint is untouched.
 */
function dropRestatedComputedConstraints(
  blockers: Blocker[],
  steps: readonly NextStep[],
): Blocker[] {
  return blockers.filter((b) => {
    if (!b.id.startsWith('bindingConstraint:')) return true;
    const rule = classifyBindingConstraint(b.title);
    if (!rule?.restatedBy) return true;
    // Dropping the restatement is only safe while the authoritative blocker can
    // still tell the user what to do. `attachRemediations` gives an `adequacy:`
    // blocker the whole ranked step list — but it returns the blocker untouched
    // when that list is EMPTY, which is the shape of a site with nothing
    // analysed at all: exactly the site 'No source reports yet' describes. On
    // that site, dropping the row carrying 'Open Analyses' leaves a critical
    // blocker with no action.
    //
    // Only rules that HAVE a route are protected. A rule with none (the coarse
    // gate, the coverage floor) contributes no action either way, so dropping
    // it loses nothing and keeps the duplicate off the Resolve count.
    if (steps.length === 0 && rule.route) return true;
    // Drop ONLY when the authoritative blocker is present AND is stating the
    // SAME thing. Checking its id alone is not enough: `adequacy:withheld` also
    // fires for 'No source reports yet' and coverage withholds, and dropping the
    // constraint against one of those would delete a live signal that nothing
    // else is surfacing. Re-running the rule over its `why` is the same test
    // that recognised the constraint in the first place.
    return !blockers.some(
      (other) => other.id === rule.restatedBy && rule.match(other.why),
    );
  });
}

function attributeCoarseConfidenceToReportQa(
  blockers: Blocker[],
  reports: readonly SiteRagReport[],
): Blocker[] {
  const qaBlockTotal = reports.reduce((n, r) => n + countQaBlocks(r), 0);
  if (qaBlockTotal === 0) return blockers;
  return blockers.map((b) => {
    if (b.id !== 'adequacy:withheld' || b.why !== COARSE_CONFIDENCE_WITHHELD_REASON) return b;
    const plural = qaBlockTotal === 1 ? '' : 's';
    return {
      ...b,
      why:
        `${COARSE_CONFIDENCE_WITHHELD_REASON}. This is the SAME problem as the `
        + `${qaBlockTotal} block-severity QA finding${plural} listed below, not a separate one — `
        + `the data-confidence penalty is aggregated from those findings, so resolving them is `
        + `what lifts this gate.`,
    };
  });
}

/**
 * Collapse overlapping firm-capacity blockers so the intent-side cap and the
 * report-level firmCapacityVerdict + the intent risk-seed never triple-count the
 * SAME firm-capacity shortfall. When an intent-side `elcc-intent:*` blocker is
 * present (the authoritative, oracle-shared one), drop the report-level
 * `elcc:*` and the `risk-seed:risk-firm-capacity-*` duplicates. Order-preserving
 * otherwise; pure.
 */
function dedupeFirmCapacity(blockers: Blocker[]): Blocker[] {
  const hasIntentElcc = blockers.some((b) => b.id.startsWith('elcc-intent:'));
  if (!hasIntentElcc) return blockers;
  return blockers.filter(
    (b) => !b.id.startsWith('elcc:') && !b.id.startsWith('risk-seed:risk-firm-capacity'),
  );
}

/** Re-export so callers can map a blocker severity to a NextStep severity. */
export function blockerToStepSeverity(severity: BlockerSeverity): NextStepSeverity {
  return severity;
}

/* ── RP2 in-place resolve eligibility (CONSERVATIVE mapping) ───────────── */

/**
 * RP2 — does a remediation step resolve IN-PLACE at a concrete SED slot? A step
 * is in-place-resolvable ONLY when it declares `resolver.action ===
 * 'resolve-in-place'` AND carries a non-empty `resolver.sedPath` (the dotted
 * variablePath to amend). This is the conservative gate the Resolve stage uses
 * to decide between "open the in-place amendment dialog" and the existing
 * open-intent / open-evidence nav route.
 *
 * WHY conservative: only a step that maps to a KNOWN, deep-enough SED slot can
 * be safely fixed at the value source — a fabricated path is rejected by
 * `applySingleAmendmentToSed`'s depth/schema guard (which would surface an
 * error). Aggregate verdict states (`band:no-go`, `red-pillar:*`,
 * `adequacy:*`) and audit-class conflicts (`elcc:*`, `bindingConstraint:*`)
 * have NO single slot, so they never carry a `sedPath` and always fall back to
 * the nav route / the decision-audit drilldown. Pure.
 */
export function isResolveInPlaceStep(step: NextStep): boolean {
  return (
    step.resolver?.action === 'resolve-in-place' &&
    typeof step.resolver.sedPath === 'string' &&
    step.resolver.sedPath.includes('.')
  );
}

/**
 * RP2 — does a blocker have AT LEAST ONE in-place-resolvable remediation? The
 * Resolve stage uses this to show the "Resolve here" affordance on a blocker
 * whose remediations carry a real SED slot, while every aggregate / audit-class
 * blocker keeps its existing nav / audit route (conservative). Pure.
 */
export function blockerHasInPlaceResolve(blocker: Blocker): boolean {
  return (blocker.remediations ?? []).some(isResolveInPlaceStep);
}

/* ── Resolve-stage ownership (SINGLE SOURCE OF TRUTH) ──────────────────── */

/**
 * Blocker-id prefixes the RESOLVE stage does NOT own. These belong to other
 * stages and must be excluded from BOTH the Resolve panel body AND the rail
 * badge count, or the two diverge (the count says "1", the body says "clear").
 *
 *   - `adequacy:`       → the adequacy gate (Generate/Decision locked; Diagnose).
 *   - `missing-pillar:` → a data-collection gap (Evidence + Generate stages).
 *   - `missing-doc:`    → a critical-doc gap (Evidence stage / docs upload).
 *   - `intent-missing:` → a declared-intent gap (Intent stage — declare the basis).
 *   - `btm-*:`          → a BTM development-readiness gate (Intent stage —
 *                         land / interconnection / water / fuel / offtake / carbon).
 *   - `attestation:`    → an operator-attestation milestone (Intent stage).
 *   - `risk-seed:`      → an intent-derived deterministic risk (its mitigation is
 *                         an Intent/Evidence next-step, not a Resolve adjudication).
 *
 * Everything else (band:no-go, elcc:*, elcc-intent:*, red-pillar:*, qa-block:*)
 * is a conflict the user adjudicates in Resolve. `elcc-intent:` parallels
 * `elcc:` — both are firm-capacity conflicts surfaced for adjudication.
 */
const NON_RESOLVE_BLOCKER_PREFIXES = [
  'adequacy:',
  // GENERATOR-QUALITY DEFECTS, not site diligence gaps. R-78 fires when the
  // report overrode a tier-1 provider fact the site ALREADY holds, R-66 on
  // citations the generator emitted that do not resolve, and the internal-number
  // reconciler when one figure disagrees between sections of one report. None of
  // them says anything about the SITE — they describe our own output, cannot be
  // closed by any user action, and have no specialist deliverable. Ranking them
  // beside "water rights unconfirmed" told the user their PROJECT was blocked
  // when their ARTIFACT was defective.
  //
  // They still block the report's trustworthiness and still withhold the verdict
  // — that runs through the data-confidence path (`aggregateQaCounts` → penalty
  // → 'C' tag → `adequacy:withheld`), which is untouched. This only removes them
  // from the site's GO-blocker list and the Resolve rail count; `buildBlockers`
  // still emits them, with their built-in "Re-run analysis" action, for a
  // report-quality surface attached to the artifact.
  'qa-block:',
  'missing-pillar:',
  'missing-doc:',
  'intent-missing:',
  'btm-',
  'attestation:',
  'risk-seed:',
] as const;

/**
 * Does this blocker belong to the RESOLVE stage? The ONE predicate the Resolve
 * panel body and the rail badge both consume, so the count and the list can
 * never disagree (the conflicts-count consistency invariant).
 */
export function isResolveBlocker(blocker: Blocker): boolean {
  return !NON_RESOLVE_BLOCKER_PREFIXES.some((p) => blocker.id.startsWith(p));
}

/** Count of resolve-owned blockers — the rail badge + headline read this. */
export function countResolveBlockers(blockers: readonly Blocker[]): number {
  return blockers.reduce((n, b) => (isResolveBlocker(b) ? n + 1 : n), 0);
}
